import { Col, Row, Modal, Form, Switch, message } from "antd";
import { useTranslation } from "react-i18next";
import { UseMutateAsyncFunction, useMutation } from "react-query";
import { CountriesType } from "./countriesType";
import { Dispatch, useEffect, useState } from "react";
import { createCountries, editCountries } from "../../../api/countries";
import Title from "../../UI/Title/Title";
import AddEditModal from "../../UI/ModalComponent/AddEditModal/addeditcomponent";
import InputName from "../../UI/InputNumberComponent/InputName";
import { AxiosResponse } from "axios";

type propsType = {
  openCountrie: boolean;
  id?: string | number;
  Countries?: CountriesType | undefined;
  setOpenCountrie: React.Dispatch<React.SetStateAction<boolean>>;
  search: string;
  filter: boolean | undefined;
  setPaginatedCountries: Dispatch<any>;
  CountriesMutation: UseMutateAsyncFunction<
    AxiosResponse<any, any>,
    unknown,
    {
      limit: number;
      q: string;
      isActive?: boolean | undefined;
    },
    unknown
  >;
};

export const AddEditCountrie = ({
  openCountrie,
  setOpenCountrie,
  Countries,
  id,
  search,
  filter,
  setPaginatedCountries,
  CountriesMutation,
}: propsType) => {
  const [t] = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [, setError] = useState("");
  const isEdit = () => {
    return !!id;
  };
  const [isActive, setIsActive] = useState<boolean>(
    Countries ? Countries?.isActive : true
  );
  const [editStatus] = useState(isEdit());
  const [country, setCountry] = useState(false);
  const [areaCode, setAreaCode] = useState(false);
  const [abbreviation, setAbbreviation] = useState(false);

  useEffect(() => {
    if (editStatus) {
      setCountry(true);
      setAreaCode(true);
      setAbbreviation(true);
    }
  }, [editStatus]);

  const handleNameChange = (value: any) => {
    setCountry(value);
  };
  const handleAreaCode = (value: any) => {
    setAreaCode(value);
  };

  const handleAbbreviation = (value: any) => {
    setAbbreviation(value);
  };

  const onError = (error: any, variables: CountriesType, context: unknown) => {
    if (setError) {
      setError(error.response.data.message);
      message.error({
        content: error.response.data.message,
        duration: 10,
        onClick: () => {
          message.destroy();
        },
      });
    }
  };

  const { mutate: mutateCreateUpdate, isLoading: isSaving } = useMutation(
    !isEdit() ? "createCountrie" : "editCountrie",
    !isEdit()
      ? (countrie: CountriesType) => createCountries(countrie)
      : (countrie: CountriesType) => editCountries(id, countrie),
    {
      onSuccess: (res) => {
        setOpen(false);
        setOpenCountrie(false);
        message.success("Complete successfully");
        CountriesMutation({
          q: search,
          isActive: filter,
          limit: 0,
        }).then((res: any) => {
          setPaginatedCountries(res.data?.data);
        });
      },
      onError,
    }
  );
  const handleFinish = () => {
    const newCountrie = form.getFieldsValue();
    for (const key in newCountrie) {
      if (newCountrie[key] === undefined) {
        if (key === "isActive") {
          newCountrie[key] = true;
        } else {
          newCountrie[key] = null;
        }
      } else if (
        typeof newCountrie[key] === "string" &&
        newCountrie[key].trim() === ""
      ) {
        newCountrie[key] = null;
      }
    }
    mutateCreateUpdate(newCountrie);
    form.resetFields();
  };

  return (
    <>
      <Modal
        data-testid="modal"
        closable={false}
        maskClosable={false}
        centered
        width={410}
        onCancel={() => setOpenCountrie(false)}
        open={openCountrie}
        cancelText={t("cancel")}
        okButtonProps={{
          style: {
            height: "40px",
            background: "#1677FF",
            color: "#fff",
            width: "100px",
          },
          disabled: !country || !areaCode || !abbreviation,
        }}
        cancelButtonProps={{
          style: {
            color: "#1677FF",
            border: "1px solid #1677FF",
            height: "40px",
            width: "100px",
          },
        }}
        okText={isEdit() ? `${t("save")}` : `${t("add")}`}
        onOk={() => {
          setOpen(true);
        }}
      >
        <Col offset={1}>
          {" "}
          <Title
            title={isEdit() ? `${t("edit-country")}` : `${t("add-country")}`}
          />
        </Col>
        {isEdit() && (
          <Col offset={1}>
            {" "}
            <span className="modaltitleEdit">
              {t("edit-details-for")} {Countries?.name}
            </span>
          </Col>
        )}
        <Form
          className="no-hover-label"
          layout="vertical"
          form={form}
          id="CountrieForm"
          onFinish={handleFinish}
        >
          <Row>
            <Col span={14} offset={1} style={{ marginTop: "20px" }}>
              <Form.Item
                initialValue={Countries?.name}
                name="name"
                label={t("country")}
                rules={[
                  {
                    required: true,
                    message: "country is required",
                  },
                ]}
              >
                <InputName
                  maxLength={50}
                  data-testid="country"
                  onChange={handleNameChange}
                />
              </Form.Item>

              <Form.Item
                name="abbreviation"
                label={t("abbreviation")}
                initialValue={Countries?.abbreviation}
                rules={[
                  {
                    required: true,
                    message: "abbreviation is required",
                  },
                ]}
              >
                <InputName
                  maxLength={5}
                  data-testid="abbreviation-input"
                  onChange={handleAbbreviation}
                />
              </Form.Item>
              <Form.Item
                name="areaCode"
                label={t("area-code")}
                initialValue={Countries?.areaCode}
                rules={[
                  {
                    required: true,
                    message: "area code is required",
                  },
                ]}
              >
                <InputName
                  maxLength={5}
                  autoComplete="off"
                  data-testid="area-code"
                  onChange={handleAreaCode}
                />
              </Form.Item>

              <Form.Item name="isActive" label={`${t("status")}`}>
                <Switch
                  checkedChildren={`${t("active")}`}
                  unCheckedChildren={`${t("inactive")}`}
                  checked={isActive}
                  disabled={isEdit() ? false : true}
                  onChange={(num: boolean) => {
                    setIsActive(num);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <AddEditModal
        form="CountrieForm"
        open={open}
        setOpen={setOpen}
        id={id}
        name={<span>{Countries?.name}</span>}
        title={`${t("countrie")}`}
        isSaving={isSaving}
      />
    </>
  );
};
